import {
  createTheme,
  ThemeProvider as MuiThemeProvider,
  PaletteMode,
  CssBaseline,
} from "@mui/material";
import * as React from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useUserPreferences } from "./UserPreferencesContainer";

// Blurple color palette for VIP features (Stripe-style)
export const BLURPLE = {
  main: "#635bff",
  light: "#e8e5ff",
  hover: "#d5d0ff",
  dark: "#4f3bff",
  contrastText: "#ffffff",
} as const;

// Create theme based on mode (light/dark)
const createAppTheme = (mode: PaletteMode) => createTheme({
  palette: {
    mode,
    primary: {
      main: mode === 'dark' ? "#FFFFFF" : "#000000",
    },
    secondary: {
      main: "#23967F",
    },
    info: {
      main: "#d7d7d7",
    },
    success: {
      main: "#3c6ce9",
    },
    warning: {
      main: "#f5c25c",
    },
    background: mode === 'dark' ? {
      default: "#121212",
      paper: "#1e1e1e",
    } : {
      paper: "#ffffff",
      default: "#00000004",
    },
  },

  zIndex: {
    mobileStepper: 8000 + 1000,
    fab: 8000 + 1050,
    appBar: 8000 + 1100,
    drawer: 8000 + 1249,
    modal: 8000 + 1250,
    snackbar: 8000 + 1400,
    tooltip: 8000 + 9999,
  },

  components: {
    MuiDataGrid: {
      styleOverrides: {
        root: {
          "--DataGrid-containerBackground": mode === 'dark' ? "#1e1e1e" : "#fbfbfb",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontWeight: 700,
          borderRadius: 24,
          boxShadow: "none",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 0,
        },
      },
    },
    // Filter variant: Clean, minimal styling for filters and form controls
    // Based on FilterDropdown and OpsCenter filter components
    // Usage: <TextField size="small" /> or <Select size="small" />
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: "transparent",
          "&.MuiInputBase-sizeSmall:not(.MuiInputBase-multiline)": {
            height: 40,
            borderRadius: "8px",
            fontSize: "0.875rem", // 14px
            fontWeight: 500,
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "grey.200",
              borderWidth: "1px",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "grey.300",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "grey.400",
              borderWidth: "1px", // No thickening on focus
            },
            "& input, & .MuiSelect-select": {
              paddingLeft: "12px",
              paddingRight: "12px",
              paddingTop: 0,
              paddingBottom: 0,
              height: "100%",
              display: "flex",
              alignItems: "center",
            },
          },
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        IconComponent: KeyboardArrowDownIcon,
      },
      styleOverrides: {
        icon: {
          color: "grey.600",
          fontSize: 20,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          marginTop: "4px",
          borderRadius: "12px",
          border: "1px solid",
          borderColor: "grey.200",
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          paddingLeft: "12px",
          paddingRight: "12px",
          paddingTop: "8px",
          paddingBottom: "8px",
          fontSize: "0.875rem",
          "&:hover": {
            backgroundColor: "grey.50",
          },
          "&.Mui-selected": {
            backgroundColor: "transparent",
            "&:hover": {
              backgroundColor: "grey.50",
            },
          },
        },
      },
    },
  },
  typography: {
    button: {
      textTransform: "none",
    },
    tabs: {
      textTransform: "none",
    },
    fontFamily: [
      "Lexend Deca",
      "Inter",
      "-apple-system",
      "BlinkMacSystemFont",
      '"Segoe UI"',
      "Roboto",
      '"Helvetica Neue"',
      "Arial",
      "sans-serif",
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(","),
  },
});

// Detect OS color scheme preference
const useSystemColorScheme = (): PaletteMode => {
  const [mode, setMode] = React.useState<PaletteMode>(() => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';
    }
    return 'light';
  });

  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      setMode(e.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return mode;
};

export const ThemeContainer: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const { preferences, loading } = useUserPreferences();
  const systemColorScheme = useSystemColorScheme();

  // Determine theme mode: saved preference > OS preference > light (default)
  const mode: PaletteMode = React.useMemo(() => {
    if (preferences?.theme) {
      return preferences.theme;
    }
    return systemColorScheme;
  }, [preferences?.theme, systemColorScheme]);

  // Memoize theme to prevent unnecessary re-creation
  const theme = React.useMemo(() => createAppTheme(mode), [mode]);

  // Show nothing while loading to prevent flash of wrong theme
  if (loading) {
    return null;
  }

  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </MuiThemeProvider>
  );
};
